import type { Catalogs, Translation } from "@amtana/i18n/catalog";

// Every line the store and add-on screens show, in English and Spanish, read through
// `useStoreCopy` (behavior/use-store-copy.ts). The products' own lines belong to the store
// contract and read through `product-lines-copy.ts`; error copy stays in `store-error-copy.ts`.

export type StoreCopyKey = keyof typeof EN;
export type StoreCatalog = typeof EN;

const EN = {
  titleStore: "Seats · {product}",
  titleProduct: "Add-on · {product}",
  navSeats: "Seats",
  crumbHome: "Home",
  crumbStore: "Seats",
  storeTitle: "Seats",
  storeDescription: "The members your plan includes, and extra seats when you need them.",
  storeDescriptionWithAddOns: "The members your plan includes, extra seats, and add-ons on top of your plan.",
  addOnsHeading: "Add-ons",
  pillActive: "Active",
  view: "View",
  upgradeTitle: "Upgrade your plan",
  upgradeDescription: "This feature is only available for higher-tier plans.",
  viewPlans: "View plans",
  pricingPlans: "Pricing plans",
  quantityLabel: "Quantity (1–{max})",
  priceEachPerMonth: "{price} each / month",
  tierDescription: "{detail} · {price} / month",
  tagCurrent: "Current",
  amountDueToday: "Amount due today",
  purchased: "Successfully purchased {name}!",
  couldNotPurchase: "Could not purchase {name}",
  processingPayment: "Processing payment...",
  update: "Update",
  purchase: "Purchase",
  paymentNote: "No commitment. Cancel anytime. Payment is simulated.",
  cancelAddon: "Cancel addon",
  cancelAddonDescription: "Are you sure you want to cancel this addon? You won't be charged for it again.",
  proceedToCancel: "Proceed to cancel",
  addonCancelled: "{name} addon cancelled successfully.",
  couldNotCancel: "Could not cancel {name}",
  notFoundTitle: "That add-on does not exist",
  backToStore: "Back to store",
  descriptionTitle: "Description",
  useThisAddonTo: "Use this addon to:",
  priceLabel: "Price",
  startingFrom: "Starting from {price} / month",
  tagsLabel: "Tags",
  developerLabel: "Developer",
} as const;

const ES: Translation<StoreCatalog> = {
  titleStore: "Puestos · {product}",
  titleProduct: "Complemento · {product}",
  navSeats: "Puestos",
  crumbHome: "Inicio",
  crumbStore: "Puestos",
  storeTitle: "Puestos",
  storeDescription: "Los miembros que incluye tu plan y puestos extra cuando los necesites.",
  storeDescriptionWithAddOns:
    "Los miembros que incluye tu plan, puestos extra y complementos además de tu plan.",
  addOnsHeading: "Complementos",
  pillActive: "Activo",
  view: "Ver",
  upgradeTitle: "Mejora tu plan",
  upgradeDescription: "Esta función solo está disponible en los planes superiores.",
  viewPlans: "Ver planes",
  pricingPlans: "Planes y precios",
  quantityLabel: "Cantidad (1–{max})",
  priceEachPerMonth: "{price} cada uno / mes",
  tierDescription: "{detail} · {price} / mes",
  tagCurrent: "Actual",
  amountDueToday: "Importe a pagar hoy",
  purchased: "¡Compraste {name}!",
  couldNotPurchase: "No pudimos comprar {name}",
  processingPayment: "Procesando el pago...",
  update: "Actualizar",
  purchase: "Comprar",
  paymentNote: "Sin compromiso. Cancela cuando quieras. El pago es simulado.",
  cancelAddon: "Cancelar complemento",
  cancelAddonDescription: "¿Seguro que quieres cancelar este complemento? No se te volverá a cobrar.",
  proceedToCancel: "Sí, cancelar",
  addonCancelled: "El complemento {name} se canceló.",
  couldNotCancel: "No pudimos cancelar {name}",
  notFoundTitle: "Ese complemento no existe",
  backToStore: "Volver a la tienda",
  descriptionTitle: "Descripción",
  useThisAddonTo: "Usa este complemento para:",
  priceLabel: "Precio",
  startingFrom: "Desde {price} / mes",
  tagsLabel: "Etiquetas",
  developerLabel: "Desarrollador",
};

export const STORE_CATALOGS: Catalogs<StoreCatalog> = { en: EN, es: ES };
